import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useTagVocabulary } from '../../hooks/useTagVocabulary';
import { useGenreTaxonomy } from '../../hooks/useGenreTaxonomy';
import GenreSubgenreFilter, { genreFilterNames } from '../genre/GenreSubgenreFilter';
import { SPICE_LANES, spiceTagsForLane } from '../../lib/tagBuckets';
import { loadExclusions, saveExclusions } from '../../lib/savedExclusions';

/**
 * "Narrow the shelf" — the reader's filter sheet.
 *
 * Replaces AdvancedFiltersModal, which had four problems worth naming so
 * nobody rebuilds them:
 *
 *   1. Its option lists came from an unpaginated book_tags read, so the
 *      vocabulary was an arbitrary first 1,000 rows. See useTagVocabulary for
 *      what that query could and could not see.
 *   2. Four of the eight buckets were hardcoded literal arrays labelled
 *      "coming soon" — Point of View, Setting & World, Representation, and no
 *      spice section at all. Those options could never match a book, because
 *      no author form produces those strings.
 *   3. Six controls (Length, Complexity, Narrative Style, Romance Level,
 *      Publication Type, Accessibility) were collected and then never sent to
 *      any query. Romance Pacing was the exception and was sent as a literal
 *      label, so choosing "Slow Burn" there quietly acted as a trope filter.
 *      Those six are experiential qualities of a book rather than tags; they
 *      belong in the craving text and its embedding, not in a chip.
 *   4. Every section was an OR against every other, so adding a filter
 *      returned MORE books. The AND now lives in matching_book_ids
 *      (20260905070000).
 *
 * WHAT IS REQUIRED HERE: nothing. The craving does the matching; this only
 * trims. There is no minimum number of anything.
 *
 * HOW THE FACETS COMBINE, once, so it is not re-derived from the UI:
 * OR within a section, AND across sections. Content warnings are the single
 * exception and are an exclusion — picking one hides books that LIST it, and
 * a book with no warning data at all still shows. Every other facet is
 * positive, so an untagged book does not match it.
 */

// How long the reader stops typing/tapping before we ask how many books
// survive. Every chip tap changes the answer, and the count is a whole-table
// query — without this, dragging through a section fires one per chip.
const COUNT_DEBOUNCE_MS = 300;

// genre is deliberately NOT a key here any more (PR3,
// docs/genre-subgenre-build-spec.md). It moved from a flat name-string
// bucket toggled like the other seven to two id arrays
// (selectedGenreIds/selectedSubgenreIds, below) -- the two-level model needs
// to know WHICH genre a subgenre belongs to, which a bare string cannot say
// on its own (the same subgenre name can sit under more than one genre).
const EMPTY = { trope: [], tone: [], trigger: [], pov: [], setting: [], representation: [] };

// The two colours in this file that are not tokens, and why.
//
// The warnings section is red-on-red, and the token pair does not survive it:
// --feedback-error (#dc2626) on --feedback-error-bg (#fee2e2) measures 3.95:1,
// under the 4.5:1 an AA reader needs. #991b1b on the same background is 6.80:1.
// #f2c2be is the border tint that sits between the two without turning the
// panel into a solid block of red. Both are measured, not eyeballed.
//
// They belong in tokens.css/tokens.js as --feedback-error-ink and
// --feedback-error-border, and they are NOT added here on purpose: those two
// files are mirrored with reanlo-author (scripts/mirrored.json), so adding a
// token means editing both repos and re-stamping the manifest. That is its own
// small change, not a rider on the filter sheet.
const WARN_INK    = '#991b1b';
const WARN_BORDER = '#f2c2be';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── Chips ───────────────────────────────────────────────────────────────────

function Chip({ on, exclude, children, onClick }) {
  const border = on ? (exclude ? 'var(--feedback-error)' : 'var(--brand-forest)') : 'var(--brand-sand)';
  const background = on ? (exclude ? 'var(--feedback-error-bg)' : 'var(--brand-forest)') : 'var(--brand-white)';
  const color = on ? (exclude ? WARN_INK : 'var(--brand-white)') : 'var(--neutral-600)';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!on}
      // An exclusion chip does the opposite of what a pressed chip normally
      // means, so a screen reader is told what pressing it actually does
      // rather than just reading the tag name back.
      aria-label={exclude ? `hide books listing ${children}${on ? ', active' : ''}` : undefined}
      style={{
        borderRadius: 'var(--radius-pill)', padding: '6px 13px', fontSize: 'var(--font-size-sm)',
        cursor: 'pointer', fontFamily: 'var(--font-ui)', transition: 'all 0.12s',
        border: `1.5px solid ${border}`, background, color, fontWeight: on ? 700 : 400,
      }}
    >
      {children}{on ? ' ✕' : ''}
    </button>
  );
}

function Section({ icon, title, sub, count, children, danger }) {
  return (
    <div style={{
      background: 'var(--brand-white)',
      border: `1.5px solid ${danger ? WARN_BORDER : 'var(--brand-sand)'}`,
      borderRadius: 'var(--radius-xl)', padding: '15px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)' }}>
          {icon} {title}
        </span>
        <span style={{ fontSize: 'var(--font-size-xxs)', letterSpacing: '0.12em', color: 'var(--neutral-500)', fontWeight: 700 }}>
          {sub}
        </span>
        {count > 0 && (
          <span style={{
            marginLeft: 'auto', background: danger ? 'var(--feedback-error)' : 'var(--brand-forest)',
            color: 'var(--brand-white)', borderRadius: 'var(--radius-pill)', padding: '2px 9px',
            fontSize: 'var(--font-size-xxs)', fontWeight: 800,
          }}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * A searchable cloud of chips.
 *
 * `emptyNote` is what shows when this bucket has no vocabulary at all. It says
 * "nothing is tagged with this yet", which is true and different from the
 * sheet-wide failure message above — a section that is genuinely empty and a
 * section that failed to load must never read the same.
 *
 * A picked chip is always on screen. Two ways it used to vanish while still
 * being applied:
 *   * it sat past the first `cap` chips, behind "show all";
 *   * it is not in `options` at all. A saved "never show me" name whose tag
 *     has since been renamed, merged or rejected drops out of the vocabulary
 *     (useTagVocabulary), but loadExclusions still puts it in picked.trigger.
 *     The count and "hiding N warnings" included it, and there was no chip to
 *     see or remove.
 * So names that are picked but not offered are shown first, marked, and can
 * be tapped off like any other chip.
 */
function ChipCloud({ options, selected, onToggle, exclude, cap = 12, searchLabel, emptyNote }) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const offered = new Set(options);
  const notOffered = selected.filter(s => !offered.has(s));
  const all = [...notOffered, ...options];

  const matching = query
    ? all.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : all;
  // Order is kept so a chip never jumps when it is tapped; past the cap, only
  // the picked ones stay.
  const visible = (showAll || query)
    ? matching
    : matching.filter((o, i) => i < cap || selected.includes(o));

  if (all.length === 0) {
    return (
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontStyle: 'italic', margin: '10px 0 0' }}>
        {emptyNote}
      </p>
    );
  }

  return (
    <>
      {options.length > 0 && (
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
          style={{
            width: '100%', margin: '10px 0', padding: '9px 13px', borderRadius: 'var(--radius-pill)',
            border: '1.5px solid var(--brand-sand)', fontSize: 'var(--font-size-sm)', outline: 'none',
            fontFamily: 'var(--font-ui)', boxSizing: 'border-box',
          }}
        />
      )}
      {options.length === 0 && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', fontStyle: 'italic', margin: '10px 0 8px' }}>
          {emptyNote}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        {visible.map(o => (
          <Chip key={o} on={selected.includes(o)} exclude={exclude} onClick={() => onToggle(o)}>{o}</Chip>
        ))}
        {!showAll && !query && matching.length > visible.length && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            style={{
              border: '1.5px dashed var(--brand-sand)', background: 'none', borderRadius: 'var(--radius-pill)',
              padding: '6px 13px', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', cursor: 'pointer',
            }}
          >show all {matching.length} ▾</button>
        )}
        {/* check-search-empty-states: local-filter — `matching` narrows an
            in-memory list of chips, not a book search. Nothing is requested,
            so there is no failure that could be mistaken for no results. */}
        {matching.length === 0 && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: 0 }}>
            No results for "{query}"
          </p>
        )}
      </div>
      {notOffered.length > 0 && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '8px 0 0', lineHeight: 1.5 }}>
          {notOffered.map(n => `“${n}”`).join(', ')} {notOffered.length === 1 ? "isn't" : "aren't"} in the tag list any more.
          {' '}{notOffered.length === 1 ? 'It may have been renamed or merged. It is' : 'They may have been renamed or merged. They are'} still
          {' '}applied. Tap to remove, and pick the new name if you want it.
        </p>
      )}
    </>
  );
}

// ── The sheet ───────────────────────────────────────────────────────────────

export default function FilterSheet({ onClose, onApply, initialFilters = {}, anyTags = [], searchQuery = '' }) {
  const isMobile = useIsMobile();
  const { loading: vocabLoading, error: vocabError, vocab } = useTagVocabulary();
  const {
    genres, subgenresById, canonSubgenresForGenre,
    loading: genreTaxonomyLoading, error: genreTaxonomyError,
  } = useGenreTaxonomy();

  const [picked, setPicked] = useState({ ...EMPTY, ...(initialFilters.picked || {}) });
  // Control B's own state -- ids, not names, same reason as EMPTY's comment
  // above. Round-tripped through initialFilters exactly like `picked` and
  // `spice` are (see handleApply's `filters:` payload below).
  const [selectedGenreIds, setSelectedGenreIds] = useState(initialFilters.selectedGenreIds || []);
  const [selectedSubgenreIds, setSelectedSubgenreIds] = useState(initialFilters.selectedSubgenreIds || []);
  const [spice, setSpice] = useState(initialFilters.spice || 'any');
  const [showMore, setShowMore] = useState(false);

  // null means "not counted yet or the count failed", which is NOT zero. Zero
  // empties the shelf and disables the button; unknown must do neither.
  const [matchCount, setMatchCount] = useState(null);
  const [countFailed, setCountFailed] = useState(false);
  const [counting, setCounting] = useState(false);

  // The reader's standing "never show me" list, loaded once on open.
  //
  // It PRE-LOADS into picked.trigger rather than being applied invisibly on
  // top. A safety filter the reader cannot see is one they cannot correct, and
  // an exclusion that silently narrows every craving with nothing on screen to
  // say so is indistinguishable from the catalogue being small.
  //
  // It does not overwrite an explicit choice: if this sheet was opened with
  // trigger chips already picked (initialFilters, a craving being refined),
  // that is the reader deciding right now and it wins.
  const [savedNote, setSavedNote] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);
  const openedWithTriggers = useRef((initialFilters.picked?.trigger || []).length > 0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, names, reason } = await loadExclusions();
      if (cancelled) return;
      if (!ok) {
        // Loudly, and not as an empty list. "You have no saved exclusions" and
        // "we could not check" mean opposite things to somebody who asked
        // never to be shown animal death.
        setSavedNote(`Your saved list could not be loaded, so it is not applied here, ${reason}`);
        return;
      }
      if (names.length === 0 || openedWithTriggers.current) return;
      setPicked(p => ({ ...p, trigger: [...new Set([...p.trigger, ...names])] }));
      setSavedNote(`${names.length} saved exclusion${names.length === 1 ? '' : 's'} applied. Change them here and press "Save as my default" to keep it.`);
    })();
    return () => { cancelled = true; };
  }, []);

  async function saveAsDefault() {
    setSavingDefault(true);
    const result = await saveExclusions(picked.trigger);
    setSavingDefault(false);
    setSavedNote(result.ok
      ? (result.names.length === 0
          ? 'Saved. You have no standing exclusions now.'
          : `Saved. ${result.names.length} exclusion${result.names.length === 1 ? '' : 's'} will apply to every craving from now on.`)
      : result.reason);
  }

  const toggle = (bucket, value) => setPicked(p => ({
    ...p,
    [bucket]: p[bucket].includes(value) ? p[bucket].filter(v => v !== value) : [...p[bucket], value],
  }));

  const clearAll = () => {
    setPicked(p => ({ ...EMPTY, trigger: p.trigger })); setSpice('any');
    setSelectedGenreIds([]); setSelectedSubgenreIds([]);
  };

  const totalActive =
    Object.values(picked).reduce((n, list) => n + list.length, 0)
    + selectedGenreIds.length + selectedSubgenreIds.length
    + (spice !== 'any' ? 1 : 0);

  // What "clear all" actually clears: the warnings stay on, so they stay out of its count.
  const clearableCount = totalActive - picked.trigger.length;

  // The exact arguments both the count and the search send, built once so the
  // number on the button cannot be produced by different filters than the
  // shelf. Named to match matching_book_ids' parameters.
  const facets = useMemo(() => ({
    search_query: searchQuery || '',
    any_tags:     anyTags,
    genre_tags:   genreFilterNames(selectedGenreIds, selectedSubgenreIds, genres, subgenresById),
    trope_tags:   picked.trope,
    tone_tags:    picked.tone,
    spice_tags:   spiceTagsForLane(spice),
    pov_tags:     picked.pov,
    setting_tags: picked.setting,
    rep_tags:     picked.representation,
    exclude_tags: picked.trigger,
  }), [picked, selectedGenreIds, selectedSubgenreIds, genres, subgenresById, spice, anyTags, searchQuery]);

  const facetsKey = JSON.stringify(facets);
  const latestRequest = useRef(0);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const requestId = ++latestRequest.current;
      setCounting(true);

      const { data, error } = await supabase.rpc('count_books_faceted', facets);

      // A slower earlier request must not overwrite a newer answer.
      if (requestId !== latestRequest.current) return;
      setCounting(false);

      if (error) {
        // Deliberately NOT setMatchCount(0). "We could not count" and "nothing
        // matches" are opposite facts, and rendering the first as the second
        // would tell the reader their filters found nothing when they may have
        // found plenty.
        console.error('count_books_faceted:', error.message);
        setCountFailed(true);
        setMatchCount(null);
        return;
      }

      setCountFailed(false);
      setMatchCount(typeof data === 'number' ? data : null);
    }, COUNT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [facetsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const knownEmpty = matchCount === 0;

  const handleApply = () => {
    onApply({
      facets,
      // Display only: the chips the results shelf shows above the cards. It is
      // NOT what gets searched — the search sends `facets`, section by section.
      chipLabels: [
        ...genreFilterNames(selectedGenreIds, selectedSubgenreIds, genres, subgenresById),
        ...(spice !== 'any' ? [SPICE_LANES.find(l => l.key === spice).label] : []),
        ...picked.trope, ...picked.tone, ...picked.pov, ...picked.setting, ...picked.representation,
      ],
      excludeLabels: picked.trigger,
      totalActive,
      matchCount,
      // Round-tripped back in as initialFilters when the sheet is reopened.
      filters: { picked, spice, selectedGenreIds, selectedSubgenreIds },
    });
    onClose();
  };

  const shellStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', alignItems: isMobile ? 'flex-end' : 'flex-start',
    justifyContent: 'center', padding: isMobile ? 0 : 'var(--space-xl)',
    overflowY: isMobile ? 'hidden' : 'auto',
  };

  return (
    <div style={shellStyle} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Narrow the shelf"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--brand-paper)',
          borderRadius: isMobile ? '20px 20px 0 0' : 'var(--radius-xxl)',
          width: '100%', maxWidth: isMobile ? '100%' : 900, margin: isMobile ? 0 : 'auto',
          boxShadow: 'var(--shadow-xl)', maxHeight: isMobile ? '92vh' : 'none',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div style={{
          padding: isMobile ? '16px 20px 12px' : '22px 28px 18px',
          borderBottom: '1px solid var(--brand-sand)', display: 'flex',
          alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-md)', flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontSize: isMobile ? 'var(--font-size-xl)' : 'var(--font-size-h4)',
              fontWeight: 800, color: 'var(--neutral-800)', margin: 'var(--space-none)',
            }}>Narrow the shelf</h2>
            <p style={{
              fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: '3px 0 0',
              maxWidth: '52ch', lineHeight: 1.55,
            }}>
              Every filter is optional. Your craving already did the matching; this just trims what&apos;s on the shelf.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexShrink: 0 }}>
            {clearableCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                style={{
                  border: '1.5px solid var(--brand-sand)', background: 'var(--brand-white)',
                  color: 'var(--brand-rust)', borderRadius: 'var(--radius-pill)', padding: '8px 16px',
                  fontSize: 'var(--font-size-sm)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >clear all · {clearableCount}</button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close filters"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 'var(--font-size-xxl)', color: 'var(--neutral-400)', lineHeight: 1,
              }}
            >×</button>
          </div>
        </div>

        {/* ── Sections ─────────────────────────────────────────────────── */}
        <div style={{ padding: isMobile ? '14px 16px' : '18px 28px', overflowY: 'auto', flex: 1 }}>
          {vocabLoading ? (
            <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', textAlign: 'center', padding: '48px 0' }}>
              Loading the tag list…
            </p>
          ) : vocabError ? (
            /* The read failed. Say so. Eight empty sections would read as
               "Reanlo has no tropes", which is the failure this whole screen
               is least able to survive being confused about. */
            <div style={{
              background: 'var(--feedback-error-bg)', border: `1.5px solid ${WARN_BORDER}`,
              borderRadius: 'var(--radius-lg)', padding: '14px 16px',
            }}>
              <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: WARN_INK, margin: '0 0 4px' }}>
                The tag list didn&apos;t load.
              </p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', margin: 0 }}>
                This is a connection problem, not an empty catalogue, close this and try again in a moment.
              </p>
              {/* The saved "never show me" list loads separately and is still
                  applied. Without the sections there would be nothing on
                  screen saying what "hiding N warnings" below refers to. */}
              {picked.trigger.length > 0 && (
                <div style={{ marginTop: 'var(--space-10)' }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: WARN_INK, fontWeight: 700, margin: '0 0 6px' }}>
                    Still hiding books that list:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {picked.trigger.map(t => (
                      <Chip key={t} on exclude onClick={() => toggle('trigger', t)}>{t}</Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-14)' }}>

              {/* Genre and spice: the two most-used dials, side by side on desktop */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr', gap: 'var(--space-14)' }}>
                <Section icon="📚" title="Genre" sub="PICK ANY · NARROW OPTIONALLY" count={selectedGenreIds.length + selectedSubgenreIds.length}>
                  <GenreSubgenreFilter
                    Chip={Chip}
                    genres={genres}
                    canonSubgenresForGenre={canonSubgenresForGenre}
                    loading={genreTaxonomyLoading}
                    error={genreTaxonomyError}
                    selectedGenreIds={selectedGenreIds}
                    setSelectedGenreIds={setSelectedGenreIds}
                    selectedSubgenreIds={selectedSubgenreIds}
                    setSelectedSubgenreIds={setSelectedSubgenreIds}
                  />
                </Section>

                <Section icon="🌶" title="Spice" sub="ONE CHOICE" count={spice !== 'any' ? 1 : 0}>
                  <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-md)', flexWrap: 'wrap' }}>
                    {SPICE_LANES.map(lane => (
                      <button
                        key={lane.key}
                        type="button"
                        onClick={() => setSpice(lane.key)}
                        aria-pressed={spice === lane.key}
                        style={{
                          borderRadius: 'var(--radius-pill)', padding: '8px 15px', fontSize: 'var(--font-size-sm)',
                          cursor: 'pointer', fontWeight: spice === lane.key ? 700 : 400,
                          border: `1.5px solid ${spice === lane.key ? 'var(--brand-forest)' : 'var(--brand-sand)'}`,
                          background: spice === lane.key ? 'var(--brand-forest)' : 'var(--brand-white)',
                          color: spice === lane.key ? 'var(--brand-white)' : 'var(--neutral-600)',
                        }}
                      >{lane.label}</button>
                    ))}
                  </div>
                  <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)', margin: '9px 0 0', lineHeight: 1.5 }}>
                    no spice = none &amp; closed door · low = mild to moderate · high = spicy to scorching.
                    The exact level the author set stays on the book page.
                  </p>
                </Section>
              </div>

              {/* Content warnings. An exclusion, and visually its own species. */}
              <Section icon="🚫" title="Never show me" sub="HIDES BOOKS LISTING THESE" count={picked.trigger.length} danger>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '6px 0 0', lineHeight: 1.5 }}>
                  Pick a warning and books that list it disappear. Books that have listed nothing at all still show,
                  a blank field is not a promise. Save these as your default and they apply to every craving, not just this one.
                </p>
                <ChipCloud
                  options={vocab.trigger} selected={picked.trigger} onToggle={v => toggle('trigger', v)}
                  exclude cap={12} searchLabel="search content warnings…"
                  emptyNote="No content warnings tagged yet."
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-6)', marginTop: 'var(--space-10)' }}>
                  <button onClick={saveAsDefault} disabled={savingDefault}
                    style={{ padding: '6px 12px', borderRadius: 'var(--radius-xxl)', border: `1px solid ${WARN_BORDER}`,
                      background: 'var(--feedback-error-bg)', color: WARN_INK, fontSize: 'var(--font-size-xs)',
                      fontWeight: 700, cursor: savingDefault ? 'default' : 'pointer', opacity: savingDefault ? 0.6 : 1 }}>
                    {savingDefault ? 'Saving…' : 'Save as my default'}
                  </button>
                  {/* Deliberately plain text and not a toast: this is the one
                      control on the sheet whose failure a reader must not miss,
                      and a toast that has already faded is the same as silence. */}
                  {savedNote && (
                    <span style={{ fontSize: 'var(--font-size-xs)', color: WARN_INK, lineHeight: 1.45 }}>{savedNote}</span>
                  )}
                </div>
              </Section>

              <Section icon="🪢" title="Tropes" sub="PICK ANY · OPTIONAL" count={picked.trope.length}>
                <ChipCloud
                  options={vocab.trope} selected={picked.trope} onToggle={v => toggle('trope', v)}
                  cap={12} searchLabel="search tropes…"
                  emptyNote="No tropes tagged yet."
                />
              </Section>

              <Section icon="🎭" title="Mood & Tone" sub="PICK ANY · OPTIONAL" count={picked.tone.length}>
                <ChipCloud
                  options={vocab.tone} selected={picked.tone} onToggle={v => toggle('tone', v)}
                  cap={10} searchLabel="search moods…"
                  emptyNote="No moods tagged yet."
                />
              </Section>

              {/* The quieter three live behind one calm expander */}
              <button
                type="button"
                onClick={() => setShowMore(m => !m)}
                aria-expanded={showMore}
                style={{
                  border: '1.5px dashed var(--brand-sand)', background: 'transparent',
                  borderRadius: 'var(--radius-xl)', padding: 'var(--space-md)', fontSize: 'var(--font-size-base)',
                  fontWeight: 700, color: 'var(--brand-rust)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
                }}
              >
                {showMore ? 'fewer filters ▴' : 'more filters · point of view, settings & world, representation ▾'}
                {!showMore && (picked.pov.length + picked.setting.length + picked.representation.length > 0) && (
                  <span style={{
                    marginLeft: 'var(--space-sm)', background: 'var(--brand-forest)', color: 'var(--brand-white)',
                    borderRadius: 'var(--radius-pill)', padding: '2px 9px', fontSize: 'var(--font-size-xxs)',
                  }}>{picked.pov.length + picked.setting.length + picked.representation.length}</span>
                )}
              </button>

              {showMore && (
                <>
                  <Section icon="👁" title="Point of View" sub="PICK ANY" count={picked.pov.length}>
                    <ChipCloud
                      options={vocab.pov} selected={picked.pov} onToggle={v => toggle('pov', v)}
                      cap={12} searchLabel="search points of view…"
                      // Until the author portal's POV picks are written as tags
                      // and the existing books are backfilled, this bucket is
                      // nearly empty. Saying "nothing is tagged yet" is the
                      // true reason, and it is not the same as a failed load.
                      emptyNote="No points of view tagged yet."
                    />
                  </Section>

                  <Section icon="🗺" title="Settings & World" sub="PICK ANY" count={picked.setting.length}>
                    <ChipCloud
                      options={vocab.setting} selected={picked.setting} onToggle={v => toggle('setting', v)}
                      cap={12} searchLabel="search settings…"
                      emptyNote="No settings tagged yet."
                    />
                  </Section>

                  <Section icon="🫶" title="Representation" sub="PICK ANY" count={picked.representation.length}>
                    <ChipCloud
                      options={vocab.representation} selected={picked.representation}
                      onToggle={v => toggle('representation', v)}
                      cap={12} searchLabel="search representation…"
                      emptyNote="No representation tags yet."
                    />
                  </Section>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Apply bar ────────────────────────────────────────────────── */}
        <div style={{
          padding: isMobile ? '12px 16px 20px' : '14px 28px 20px',
          borderTop: '1px solid var(--brand-sand)', background: 'var(--brand-white)',
          borderRadius: isMobile ? 0 : '0 0 var(--radius-xxl) var(--radius-xxl)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'var(--space-md)', flexWrap: 'wrap', flexShrink: 0,
        }}>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)' }}>
            {totalActive === 0 ? 'no filters, the whole shelf' : `${totalActive} filter${totalActive === 1 ? '' : 's'} active`}
            {picked.trigger.length > 0 && (
              <span style={{ color: 'var(--feedback-error)' }}>
                {' '}· hiding {picked.trigger.length} warning{picked.trigger.length === 1 ? '' : 's'}
              </span>
            )}
          </span>

          <div style={{ display: 'flex', gap: 'var(--space-10)', alignItems: 'center', flexWrap: 'wrap' }}>
            {knownEmpty && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-error)', fontWeight: 600 }}>
                nothing survives these filters, loosen one
              </span>
            )}
            {countFailed && (
              /* Not "0 books". The count is the thing that failed, not the
                 shelf, so the button stays usable and says why. */
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
                couldn&apos;t count these, the shelf will still load
              </span>
            )}
            <button
              type="button"
              disabled={knownEmpty}
              onClick={handleApply}
              style={{
                background: knownEmpty ? 'var(--brand-sand)' : 'var(--brand-rust)',
                color: knownEmpty ? 'var(--neutral-500)' : 'var(--brand-white)',
                border: 'none', borderRadius: 'var(--radius-lg)', padding: '13px 28px',
                fontSize: 'var(--font-size-md)', fontWeight: 800,
                cursor: knownEmpty ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)',
              }}
            >
              {knownEmpty
                ? 'no books to show'
                : counting || matchCount === null
                  ? 'show results'
                  : `show ${matchCount} book${matchCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
